import { iaRa, inRn } from 'lib/helpers/utility';

import { processInvoices } from '../components/Invoices/lib/helpers';
import { calculateSurcharge } from '../components/PaymentSummary/utils/helpers';

import { ITEM_TYPES, itemsTotal } from './itemTypes';

// Methods and modes that hand the patient a link to pay later: a tip can't be collected there.
export const TIPLESS_METHODS = ['request-payment'];
export const TIPLESS_CHECKOUT_MODES = ['send-invoice'];

export const isTiplessCheckout = ({ method, checkoutMode } = {}) =>
  TIPLESS_METHODS.includes(method) || TIPLESS_CHECKOUT_MODES.includes(checkoutMode);

export const isChargedToday = (values = {}) =>
  !isTiplessCheckout(values) && !values.recurringPayment;

export const NO_TECH_FEE_METHODS = ['cash', 'check', 'outside-financing'];

export const isTechFeeMethod = (method) => !NO_TECH_FEE_METHODS.includes(method);

export const toSurchargeRate = (percentage) => {
  const value = Number(percentage);
  return Number.isFinite(value) && value > 0 ? value / 100 : 0;
};

export const resolveTechFeeAmounts = ({ values, isEnabled, surchargePercentage = 0 }) => {
  const baseAmount = Math.max(0, inRn(values?.amount) - inRn(values?.surchargeAmount));
  const saleAmount = baseAmount + inRn(values?.appliedBalance) - inRn(values?.gratuity);
  const surchargeAmount = isEnabled
    ? calculateSurcharge(Math.max(0, saleAmount), surchargePercentage)
    : 0;

  return { amount: baseAmount + surchargeAmount, surchargeAmount };
};

/** What is actually due for the sale: the charged amount without the tip. */
export const amountWithoutGratuity = ({ amount, gratuity } = {}) =>
  Math.max(0, Math.round(inRn(amount) - inRn(gratuity)));

export const resolveItemsDifference = ({
  hasItems,
  subtotal,
  amount,
  appliedBalance,
  surchargeAmount,
  gratuity
}) => {
  const difference = hasItems ? amount + appliedBalance - surchargeAmount - gratuity - subtotal : 0;

  return {
    difference,
    amountOverItems: Math.max(0, difference),
    remainingItemsBalance: Math.max(0, -difference)
  };
};

/**
 * Re-prices the cart once its catalog items were billed into real invoices.
 *
 * The tip and applied credit are carried over, custom invoice amounts stay on the invoices they
 * were typed for, and a partial amount the staff typed is kept (capped at the new total).
 * `isAmountChanged` flags a total that no longer matches what the charge button showed —
 * e.g. tax the server added — so the caller can stop and ask instead of charging it silently.
 */
export const resolveMaterializedAmounts = ({
  values,
  createdInvoices,
  isSurchargeEnabled = false,
  surchargePercentage = 0
}) => {
  const items = iaRa(values?.items);
  const catalogItems = items.filter((item) => item?.type !== ITEM_TYPES.INVOICE);
  const keptItems = items.filter((item) => item?.type === ITEM_TYPES.INVOICE);
  const keptInvoices = processInvoices(keptItems, values?.customPaymentAmounts || {});

  const createdItems = iaRa(createdInvoices).map((invoice) => ({
    ...invoice,
    type: ITEM_TYPES.INVOICE,
    balance: inRn(invoice?.balance ?? invoice?.total_amount)
  }));

  const surchargeOf = (subtotal) =>
    isSurchargeEnabled ? calculateSurcharge(subtotal, surchargePercentage) : 0;
  const gratuity = inRn(values?.gratuity);
  const appliedBalance = inRn(values?.appliedBalance);
  const totalFor = (subtotal) =>
    Math.max(0, subtotal + surchargeOf(subtotal) + gratuity - appliedBalance);

  const previousSubtotal = itemsTotal([...catalogItems, ...keptInvoices]);
  const nextSubtotal = itemsTotal([...keptInvoices, ...createdItems]);
  const previousTotal = totalFor(previousSubtotal);
  const nextTotal = totalFor(nextSubtotal);

  const shownAmount = inRn(values?.amount);
  const isPartialAmount = shownAmount !== previousTotal;
  const overage = Math.max(0, shownAmount - previousTotal);
  const amount =
    overage > 0
      ? nextTotal + overage
      : isPartialAmount
        ? Math.min(shownAmount, nextTotal)
        : nextTotal;

  return {
    items: [...keptItems, ...createdItems],
    invoices: [...keptInvoices, ...createdItems],
    amount,
    surchargeAmount: isPartialAmount ? inRn(values?.surchargeAmount) : surchargeOf(nextSubtotal),
    shownAmount,
    isAmountChanged: amount !== shownAmount
  };
};

/**
 * The amount fields for a new cart selection. The tip rides on top of the items and the applied
 * credit is kept (never more than the new total), so the summary and the charge stay in sync.
 */
export const resolveSelectionAmounts = ({ subtotal, surchargeAmount = 0, values }) => {
  const gratuity = subtotal > 0 ? inRn(values?.gratuity) : 0;
  const total = subtotal + surchargeAmount + gratuity;
  const appliedBalance = Math.min(inRn(values?.appliedBalance), total);

  return { amount: total - appliedBalance, appliedBalance, gratuity, surchargeAmount };
};
